import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Map, Plus } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PLAN_STATUS_LABELS, PLAN_STATUS_STYLES } from "@/config/journeyCatalog";

export default function JourneyPlansTab({ child }) {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canManage = hasPermission("journeys:manage");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journey-plans", { params: { child_id: child.id } });
      setItems(data.items);
    } catch (err) {
      if (err?.response?.status !== 404) toast.error(formatApiError(err));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [child.id]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (planType = "primary") => {
    setCreating(planType);
    try {
      const { data } = await api.post(`/journey-plans/children/${child.id}`, { plan_type: planType });
      toast.success(`Route plan ${data.plan_number} created`);
      navigate(`/journey-plans/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="journey-plans-tab">
      {canManage && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-slate-500 max-w-xl">
            Plans support separate Home → School and School → Home routes, landmarks, crossings, risk assessment,
            alternative routes and an emergency plan. Manager approval is required before training begins.
          </p>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button onClick={() => create("primary")} disabled={!!creating} data-testid="journey-plan-create-button"
              className="h-10 bg-primary hover:bg-brand-hover text-white font-semibold">
              {creating === "primary" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" /> New primary route plan</>}
            </Button>
            <Button variant="outline" onClick={() => create("alternative")} disabled={!!creating} data-testid="alternative-plan-create-button"
              className="h-10 font-semibold">
              {creating === "alternative" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" /> New alternative route plan</>}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center" data-testid="journey-plans-empty">
          <Map className="mx-auto h-8 w-8 text-slate-300 mb-3" />
          <p className="text-sm text-slate-600">No route plans yet for this case.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="journey-plans-list">
          {items.map((p) => (
            <Card key={p.id} data-testid={`journey-plan-row-${p.id}`} className="hover:border-primary/40 transition-colors duration-200">
              <CardContent className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => navigate(`/journey-plans/${p.id}`)} data-testid={`journey-plan-open-${p.id}`}
                      className="font-medium text-primary hover:underline text-left">
                      {p.plan_number} — v{p.version}
                    </button>
                    <Badge className={PLAN_STATUS_STYLES[p.status]} data-testid={`journey-plan-status-${p.id}`}>
                      {PLAN_STATUS_STYLES[p.status] ? PLAN_STATUS_LABELS[p.status] : p.status}
                    </Badge>
                    {p.plan_type === "alternative" && (
                      <Badge className="bg-amber-50 text-amber-700 border-transparent" data-testid={`journey-plan-type-${p.id}`}>
                        Alternative / emergency
                      </Badge>
                    )}
                    {p.review_required && (
                      <Badge className="bg-red-50 text-red-700 border-transparent" data-testid={`journey-plan-review-required-${p.id}`}>
                        Review required
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Created by {p.creator_name || "—"} · {new Date(p.created_at).toLocaleDateString("en-GB")}
                    {p.review_date ? ` · review by ${p.review_date}` : ""}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
